import { CarrierPage } from '@/page/domain/CarrierPage';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';

const { getIaFQuoteUrl } = useUrlHelper();

export const aetnaPageData: Partial <CarrierPage> = {
  logoSection: {
    visible: true,
    title: 'Aetna Insurance Coverage',
    content: `Aetna is no longer available through Covered California. <a class="underline" href="${getIaFQuoteUrl()}">Click here </a> to view an updated list of carriers.`
  },
  aboutInfo: {
    visible: true,
    title: 'About Aetna',
    content: `<p>Aetna is a health insurance provider owned by CVS Health. It’s one of the oldest and largest health insurance companies in the country, with a history <a class="underline" href="https://www.aetna.com/about-us/aetna-history.html#tab_content_section_responsivegrid_88256254_responsivegrid_tabs_link_tabs_1" target="_blank">dating back to the 1800s</a>. Aetna got its start selling life insurance. Today, it also offers various medical and dental plans nationwide.</p><br>
    <p>Aetna wasn’t always available in California. In 2018, the California Department of Health Care Services (DHCS) approved Aetna joining Medi-Cal with its <a class="underline" href="https://www.aetnabetterhealth.com/california/index.html" target="_blank">Better Health of California</a> plan, offered in San Diego and Sacramento counties. Then, in 2023, Aetna CVS Health joined Covered California to offer individual and family health insurance. At the end of 2025, Aetna exited the state marketplace.</p>`
  },
  sectionInfo: {
    visible: true,
    title: 'What Is Aetna Known For?',
    content: '<p>People choose Aetna for its vast network of providers, competitive rates and long history in the insurance industry. It also offers benefits like access to 24/7 virtual care at no or low costs, depending on your plan, and mental health coverage. Aetna is regarded as a good health insurance provider overall.</p>',
    imageSection: {
      title: 'Aetna offers benefits like access to <b>24/7 virtual care</b> at no or low costs, depending on your plan, and <b>mental health coverage</b>.',
      titleColor: 'text-white',
      titleSeparator: true,
      titleSeparatorColor: 'bg-sky-300',
      contentWidth: 'w-4/5 sm:w-1/2',
      justifyContent: 'items-end',
      textDirection: 'text-left',
      redirectButtonText: 'Contact Us',
      redirectButtonUrl: getIaFQuoteUrl(),
      imageUrl: '/assets/img/carriers/aetna-info-banner.webp',
      imageClass: 'min-w-[800px] left-1/5 transform -translate-x-1/5 sm:min-w-[1000px] sm:left-1/2 sm:-translate-x-1/2'
    },
  },
  extraSection2: {
    visible: true,
    contentDirection: 'right',
    title: 'What Does Aetna CVS Health Cover?',
    content: '<p>Aetna CVS Health is compliant with the Affordable Care Act (ACA), meaning <a class="underline" href="https://www.aetnacvshealth.com/buying-aca-coverage/plan-basics.html" target="_blank">it covers essential benefits</a> such as:</a></p><br>',
    listContent: {
      list: [
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Medical care for adults and children</div',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Mental health services</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Maternity care</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Hospitalization and emergency services</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Prescriptions</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Lab services</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Preventive care</div>',
        '<div class="flex flex-row"><div class="mt-1 mr-2 rounded-full bg-yellow-500 w-3 h-3"></div>Chronic disease management</div>',
      ],
      listCut: 4,
    },
    secondContent: '<p>As a Medi-Cal beneficiary with an Aetna plan, you’ll also have dental care benefits with Denti-Cal. Your medications will be covered under Medi-Cal Rx rather than Aetna.</p>',
    textDirection: 'text-left',
    imageUrl: '/assets/img/carriers/aetna-doctor-and-old-man.webp',
    bgColor: 'bg-sky-50'
  },
  extraSection3: {
    visible: true,
    contentDirection: 'right',
    title: 'Is Aetna Better Than Kaiser Permanente?',
    content: '<p>The National Committee for Quality Assurance (NCQA) gives Kaiser Permanente a higher rating than Aetna plans overall. These ratings are based on patient happiness, the quality of care received and the plan\'s efforts to improve. You can review any health plan\'s ratings on the NCQA site <a class="underline" href="https://reportcards.ncqa.org/health-plans" target="_blank">here</a>.</p>',
    textDirection: 'text-center'
  },
  finalSection: {
    visible: true,
    contentDirection: 'right',
    title: 'Learn More About Health Insurance Options With Us',
    content: `<p>Do you have more questions about Aetna health insurance? We're here for you. At Health for California, we understand that applying for health insurance can be confusing. You might have a lot of questions or need clarification about the best options available to you and your family.</p><br>
    <p>Our agents can answer questions about Aetna or any Covered California health plan. We're happy to help you find an affordable health plan stress-free.</p><br>
    <p>If you're ready to apply for health insurance, our online application process is quick and simple. Get started today <a class="underline" href="${getIaFQuoteUrl()}">with a free quote.</p>`,
    textDirection: 'text-center',
    imageSection: {
      title: 'Learn More About Aetna With Us',
      contentWidth: 'w-3/5 sm:w-1/2',
      extraSpace: 'py-10',
      justifyContent: 'items-end',
      textDirection: 'text-left',
      redirectButtonText: 'Get A Free Quote',
      redirectButtonUrl: getIaFQuoteUrl(),
      imageUrl: '/assets/img/carriers/aetna-final-banner.webp',
      imageClass: 'min-w-[800px] left-1/5 transform -translate-x-1/5 sm:min-w-[1000px] sm:left-1/2 sm:-translate-x-1/2'
    },
    bgColor: 'bg-sky-50'
  },
};
